import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import DefaultMediumImage from "../assets/defaultm.jpg";
import SectionTitle from "./SectionTitle";
import { blog, site } from "../content/home";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] } }),
};

const MediumBlog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMediumArticles = async () => {
      try {
        const response = await fetch(site.mediumRssUrl, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        const data = await response.json();
        if (data.status === "ok") {
          setArticles(Array.isArray(data.items) ? data.items.slice(0, 3) : []);
        }
      } catch (e) {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMediumArticles();
  }, []);

  const extractImageUrl = (content) => {
    const m = content && content.match(/<img[^>]+src="([^">]+)"/);
    return m && m[1] ? m[1].split("?")[0] : null;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html || "";
    return tmp.textContent || tmp.innerText || "";
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (loading) {
    return (
      <section id="blog" className="relative scroll-mt-24 py-24">
        <div className="mx-auto max-w-7xl container-px text-center text-gray-500 dark:text-white/50">
          {blog.loading}
        </div>
      </section>
    );
  }
  if (articles.length === 0) return null;

  return (
    <section id="blog" className="relative scroll-mt-24 py-24">
      <div className="mx-auto max-w-7xl container-px">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="section-eyebrow mb-4">
          <span className="h-px w-8 bg-brand-500" /> {blog.eyebrow}
        </motion.div>
        <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}>
          <SectionTitle lead={blog.title.lead} accent={blog.title.accent} tail={blog.title.tail} className="max-w-2xl" />
        </motion.div>
        <motion.p variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-3 max-w-xl text-gray-700 dark:text-white/70">
          {blog.subtitle}
        </motion.p>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a, i) => {
            const img = extractImageUrl(a.content) || DefaultMediumImage;
            const desc = stripHtml(a.description).substring(0, 140);
            return (
              <motion.a
                key={i}
                href={a.link}
                target="_blank"
                rel="noopener noreferrer"
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-black/10 bg-white/60 transition-colors hover:border-black/20 dark:border-white/10 dark:bg-white/[0.02] dark:hover:border-white/20"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={img}
                    alt=""
                    onError={(e) => { e.target.src = DefaultMediumImage; }}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="mb-2 flex items-center gap-2 text-xs text-gray-500 dark:text-white/50">
                    <span>{formatDate(a.pubDate)}</span>
                    {a.categories && a.categories[0] && (
                      <span className="rounded-full bg-black/[0.05] px-2 py-0.5 dark:bg-white/5">{a.categories[0]}</span>
                    )}
                  </div>
                  <h3 className="font-display text-base font-semibold text-gray-900 group-hover:gradient-text dark:text-white">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm text-gray-700 dark:text-white/70">{desc}…</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-500 group-hover:text-brand-600 dark:group-hover:text-white">
                    {blog.readMore}
                    <svg viewBox="0 0 512 512" className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="currentColor">
                      <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z" />
                    </svg>
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <a
            href={site.socials.medium}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
          >
            {blog.viewAll}
            <svg viewBox="0 0 512 512" className="h-4 w-4" fill="currentColor">
              <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default MediumBlog;